/**
 * Offline Cache - Persistencia de datos para modo offline
 * Implementación con localStorage (simple) con comentarios para migración a IndexedDB.
 * 
 * NOTA: localStorage tiene limitaciones:
 * - Límite ~5-10MB
 * - Solo strings (requiere JSON.stringify/parse)
 * - Síncrono (bloquea main thread)
 * 
 * TODO: Migrar a IndexedDB para:
 * - Mayor capacidad (50MB+)
 * - Soporte para blobs/imágenes
 * - Queries indexadas
 * - Operaciones async
 */

// Claves de almacenamiento
const STORAGE_KEYS = {
  CATALOG: 'corazonadas_catalog',
  LAST_SYNC: 'corazonadas_last_sync',
  DEVICE_ID: 'corazonadas_device_id'
};

// Límite de tamaño aproximado para localStorage (en bytes)
const STORAGE_LIMIT = 5 * 1024 * 1024; // 5MB

/**
 * Guarda el catálogo de eventos en cache
 * @param {Array} events - Lista de eventos a cachear
 * @returns {boolean} Éxito de la operación
 */
export const cacheCatalog = (events) => {
  try {
    // Validar que es un array
    if (!Array.isArray(events)) {
      console.warn('[Cache] ❌ Se esperaba un array de eventos');
      return false;
    }
    
    // Calcular tamaño estimado
    const data = JSON.stringify(events);
    const size = new Blob([data]).size;
    
    if (size > STORAGE_LIMIT) {
      console.warn('[Cache] ⚠️ Datos exceden límite de localStorage (~5MB)');
      // TODO: Implementar paginación o compresión
      return false;
    }
    
    // Guardar en localStorage
    localStorage.setItem(STORAGE_KEYS.CATALOG, data);
    localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
    
    console.log('[Cache] ✅ Catálogo guardado:', events.length, 'eventos,', (size / 1024).toFixed(2), 'KB');
    return true;
    
  } catch (error) {
    // Manejar errores comunes de localStorage
    if (error.name === 'QuotaExceededError') {
      console.error('[Cache] ❌ Quota exceeded - Limpiando espacio...');
      cleanupStorage();
    } else {
      console.error('[Cache] ❌ Error al guardar:', error.message);
    }
    return false;
  }
};

/**
 * Lee el catálogo cacheado
 * @returns {Array|null} Lista de eventos o null si no hay cache
 */
export const readCatalog = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CATALOG);
    
    if (!data) {
      console.log('[Cache] ℹ️ No hay catálogo en cache');
      return null;
    }
    
    const events = JSON.parse(data);
    const lastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
    
    console.log('[Cache] ✅ Catálogo recuperado:', events.length, 'eventos,', 
      'última sync:', new Date(Number(lastSync)).toLocaleString());
    
    return events;
    
  } catch (error) {
    console.error('[Cache] ❌ Error al leer:', error.message);
    return null;
  }
};

/**
 * Obtiene o genera un deviceId único para este dispositivo
 * @returns {string} DeviceId
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  
  if (!deviceId) {
    deviceId = 'device-' + Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
    console.log('[Cache] 🆔 Nuevo deviceId generado:', deviceId);
  }
  
  return deviceId;
};

/**
 * Limpia el cache de catálogo
 */
export const clearCatalog = () => {
  localStorage.removeItem(STORAGE_KEYS.CATALOG);
  localStorage.removeItem(STORAGE_KEYS.LAST_SYNC);
  console.log('[Cache] 🗑️ Catálogo limpiado');
};

/**
 * Limpieza de storage cuando se excede quota
 * Elimina items antiguos manteniendo deviceId
 */
const cleanupStorage = () => {
  const deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  
  // Eliminar todo excepto deviceId
  localStorage.clear();
  
  if (deviceId) {
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  }
  
  console.log('[Cache] 🧹 Storage limpiado (conservando deviceId)');
};

/**
 * Verifica si hay datos cacheados válidos
 * @param {number} [maxAge=3600000] - Edad máxima en ms (default: 1 hora)
 * @returns {boolean}
 */
export const hasValidCache = (maxAge = 3600000) => {
  const lastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
  
  if (!lastSync) return false;
  
  const age = Date.now() - Number(lastSync);
  return age < maxAge;
};

/**
 * Estadísticas del cache (para debugging)
 * @returns {Object}
 */
export const getCacheStats = () => {
  const catalog = localStorage.getItem(STORAGE_KEYS.CATALOG);
  const lastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
  
  return {
    hasCatalog: !!catalog,
    catalogSize: catalog ? new Blob([catalog]).size : 0,
    lastSync: lastSync ? new Date(Number(lastSync)) : null,
    deviceId: getDeviceId()
  };
};
